import { useState } from "react";
import { HttpError, request } from "../../../api/http";
import { useConfirm } from "../../../components/Dialog";
import { IconClose } from "../../../components/Icons";
import { CancellationRequestDto } from "../../../types/cancellation";

const today = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default function CompleteCancellationRequestModal({
  row,
  token,
  onClose,
  onApplied,
}: {
  row: CancellationRequestDto;
  token: string | null;
  onClose: () => void;
  onApplied: () => void;
}) {
  const confirm = useConfirm();
  const [effectiveDate, setEffectiveDate] = useState(today());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!effectiveDate) {
      setError("La fecha efectiva es obligatoria");
      return;
    }
    const ok = await confirm({
      title: "Completar solicitud",
      message:
        "Esto cancelará el lead en el sistema legacy y enviará el email final al cliente. Esta acción no se puede deshacer. ¿Continuar?",
      confirmLabel: "Completar",
      tone: "warning",
    });
    if (!ok) return;
    setSubmitting(true);
    setError(null);
    try {
      await request(
        `/api/admin/operations/cancellation-requests/${row.id}/complete`,
        {
          method: "PATCH",
          token,
          body: { effectiveDate },
        },
      );
      onApplied();
    } catch (err) {
      setError(
        err instanceof HttpError
          ? `HTTP ${err.status}: ${
              ((err.body as { message?: string })?.message ?? "").slice(0, 200) ||
              ((err.body as { error?: string })?.error ?? "")
            }`
          : "No se pudo completar",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.55)",
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(420px, 92vw)",
          background: "white",
          borderRadius: "0.55rem",
          boxShadow: "0 10px 32px rgba(0,0,0,.2)",
          whiteSpace: "normal",
          wordBreak: "break-word",
          textAlign: "left",
        }}
      >
        <header
          style={{
            display: "flex",
            alignItems: "center",
            padding: "0.85rem 1rem",
            borderBottom: "1px solid #e9ecef",
            gap: "0.75rem",
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: ".95rem",
              color: "#1f2933",
              flex: 1,
            }}
          >
            Completar solicitud de cancelación
          </h3>
          <button type="button" onClick={onClose} style={closeBtn}>
            <IconClose />
          </button>
        </header>

        <div style={{ padding: "1rem" }}>
          <p
            style={{
              margin: "0 0 0.85rem",
              fontSize: ".82rem",
              color: "#73879C",
            }}
          >
            Solicitud de <strong>{row.tomadorFirstName} {row.tomadorLastName}</strong>
            {" "}— póliza <strong>{row.policyNumber}</strong>.
          </p>

          <label style={{ display: "grid", gap: "0.3rem" }}>
            <span
              style={{
                fontSize: ".72rem",
                color: "#73879C",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: ".3px",
              }}
            >
              Fecha efectiva carrier
            </span>
            <input
              type="date"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
              disabled={submitting}
              style={{
                width: "100%",
                padding: "0.45rem 0.6rem",
                fontSize: ".88rem",
                border: "1px solid #dee2e6",
                borderRadius: "0.4rem",
                background: "#f8f9fb",
              }}
            />
          </label>

          {error && (
            <div
              style={{
                marginTop: "0.7rem",
                padding: "0.5rem 0.7rem",
                borderRadius: "0.4rem",
                background: "#fde2e2",
                color: "#b32424",
                fontSize: ".8rem",
              }}
            >
              {error}
            </div>
          )}

          <div
            style={{
              display: "flex",
              gap: "0.5rem",
              justifyContent: "flex-end",
              marginTop: "1rem",
            }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              style={ghostBtn}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={submitting}
              style={primaryBtn}
            >
              {submitting ? "Completando…" : "Completar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const closeBtn: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "1.85rem",
  height: "1.85rem",
  padding: 0,
  borderRadius: "0.4rem",
  background: "#f8f9fb",
  color: "#1f2933",
  border: "1px solid #dee2e6",
  cursor: "pointer",
};

const ghostBtn: React.CSSProperties = {
  padding: "0.45rem 0.95rem",
  borderRadius: "0.4rem",
  background: "white",
  color: "#73879C",
  border: "1px solid #dee2e6",
  fontSize: ".82rem",
  fontWeight: 600,
  cursor: "pointer",
};

const primaryBtn: React.CSSProperties = {
  padding: "0.45rem 1.1rem",
  borderRadius: "0.4rem",
  background: "#0069d9",
  color: "white",
  border: "1px solid #0069d9",
  fontSize: ".82rem",
  fontWeight: 600,
  cursor: "pointer",
};
